import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PetsRoundedIcon from "@mui/icons-material/PetsRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import emailjs from "@emailjs/browser";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.ts";
import { breedSuggestions, classifyBreed } from "../data/breedRules.ts";

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
}));

const ExistingCustomerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.25),
  borderRadius: theme.spacing(1.5),
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : theme.palette.secondary.light,
  marginBottom: theme.spacing(3),
}));

const QuoteBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#FFF9FB",
}));

type QuoteForm = {
  serviceType: string;
  startDate: string;
  endDate: string;
  dogCount: string;
  arrivalTime: string;
  departureTime: string;
};

type QuoteLine = {
  description: string;
  amount: number;
  unit_price: number;
  discount_percent: number;
  total: number;
};

type QuoteResult = {
  available: boolean;
  availability_status: "available" | "manual_review" | "unavailable";
  availability_message: string;
  service_type: string;
  start_date: string;
  end_date: string;
  dog_count: number;
  arrival_time: string | null;
  departure_time: string | null;
  total_amount: number;
  price_lines: QuoteLine[];
  note: string;
};

type OwnerForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
};

type InformationForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dogName: string;
  dogBreed: string;
  topic: string;
  serviceType: string;
  startDate: string;
  endDate: string;
  question: string;
};

type DogForm = {
  name: string;
  breed: string;
  mixDetails: string;
  sex: string;
  birthDate: string;
  castrationStatus: string;
  femaleStatus: string;
  heatHistoryStatus: string;
  lastHeatDate: string;
  nextHeatMonth: string;
};

type DogError = Partial<Record<keyof DogForm, string>>;

const emptyQuote: QuoteForm = {
  serviceType: "",
  startDate: "",
  endDate: "",
  dogCount: "1",
  arrivalTime: "14:00",
  departureTime: "10:00",
};

const emptyOwner: OwnerForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
};

const emptyInformation: InformationForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dogName: "",
  dogBreed: "",
  topic: "",
  serviceType: "",
  startDate: "",
  endDate: "",
  question: "",
};

const emptyDog = (): DogForm => ({
  name: "",
  breed: "",
  mixDetails: "",
  sex: "",
  birthDate: "",
  castrationStatus: "",
  femaleStatus: "",
  heatHistoryStatus: "",
  lastHeatDate: "",
  nextHeatMonth: "",
});

const friendlyBreedMessage =
  "Bedankt voor je interesse in Pippaya. Omdat we bewust kleinschalig werken en zorgvuldig kijken naar wat past binnen onze opvang en de samenstelling van de groep, kunnen we voor dit ras of rastype helaas geen nieuwe opvangaanvraag aannemen. We hopen dat je een fijne, passende opvangplek vindt voor je hond.";

export const ContactForm: React.FC = () => {
  const navigate = useNavigate();

  const [contactMode, setContactMode] =
    useState<"booking" | "information">("booking");

  const [information, setInformation] =
    useState<InformationForm>(emptyInformation);
  const [informationBusy, setInformationBusy] = useState(false);
  const [informationError, setInformationError] = useState("");
  const [informationSubmitted, setInformationSubmitted] = useState(false);

  const [quoteForm, setQuoteForm] = useState<QuoteForm>(emptyQuote);
  const [quoteResult, setQuoteResult] = useState<QuoteResult | null>(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const [showApplication, setShowApplication] = useState(false);
  const [owner, setOwner] = useState<OwnerForm>(emptyOwner);
  const [dogs, setDogs] = useState<DogForm[]>([emptyDog()]);
  const [ownerErrors, setOwnerErrors] =
    useState<Partial<Record<keyof OwnerForm, string>>>({});
  const [dogErrors, setDogErrors] = useState<DogError[]>([{}]);

  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const quoteIsSingleDay =
    quoteForm.serviceType === "dagopvang" ||
    quoteForm.serviceType === "feestdagopvang";

  const dogOutcomes = useMemo(
    () =>
      dogs.map((dog) =>
        classifyBreed(dog.breed, dog.mixDetails)
      ),
    [dogs]
  );

  const anyExcluded = dogOutcomes.some(
    (outcome) => outcome === "excluded"
  );

  const money = (value: number) =>
    new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value));

  const halfHourOptions = (startHour: number, endHour: number) => {
    const values: string[] = [];
    for (let hour = startHour; hour <= endHour; hour += 1) {
      values.push(`${String(hour).padStart(2, "0")}:00`);
      if (hour < endHour) {
        values.push(`${String(hour).padStart(2, "0")}:30`);
      }
    }
    return values;
  };

  const arrivalOptions = [...halfHourOptions(8, 23), "23:30"];
  const departureOptions = halfHourOptions(0, 18);

  const setInformationValue = (
    name: keyof InformationForm,
    value: string
  ) => {
    setInformation((prev) => ({ ...prev, [name]: value }));
    setInformationError("");
    setInformationSubmitted(false);
  };

  async function submitInformationRequest() {
    setInformationError("");
    setInformationSubmitted(false);

    if (!supabase) {
      setInformationError(
        "De informatieaanvraag is tijdelijk niet beschikbaar."
      );
      return;
    }

    if (information.firstName.trim().length < 2) {
      setInformationError("Vul je voornaam in.");
      return;
    }

    if (information.lastName.trim().length < 2) {
      setInformationError("Vul je achternaam in.");
      return;
    }

    if (
      !information.email.includes("@") ||
      information.email.trim().length < 5
    ) {
      setInformationError("Vul een geldig e-mailadres in.");
      return;
    }

    if (information.question.trim().length < 5) {
      setInformationError("Vul je vraag in.");
      return;
    }

    if (
      information.startDate &&
      information.endDate &&
      information.endDate < information.startDate
    ) {
      setInformationError(
        "De einddatum kan niet vóór de begindatum liggen."
      );
      return;
    }

    setInformationBusy(true);

    const { error: rpcError } = await supabase.rpc(
      "submit_information_request_v1",
      {
        p_first_name: information.firstName.trim(),
        p_last_name: information.lastName.trim(),
        p_email: information.email.trim().toLowerCase(),
        p_phone: information.phone.trim() || null,
        p_dog_name: information.dogName.trim() || null,
        p_dog_breed: information.dogBreed.trim() || null,
        p_topic: information.topic || null,
        p_service_type: information.serviceType || null,
        p_start_date: information.startDate || null,
        p_end_date: information.endDate || null,
        p_question: information.question.trim(),
      }
    );

    setInformationBusy(false);

    if (rpcError) {
      setInformationError(rpcError.message);
      return;
    }

    setInformationSubmitted(true);
    setInformation(emptyInformation);
  }

  const setQuoteValue = (name: keyof QuoteForm, value: string) => {
    setQuoteForm((prev) => {
      const next = { ...prev, [name]: value };

      if (
        name === "serviceType" &&
        (value === "dagopvang" || value === "feestdagopvang")
      ) {
        next.endDate = "";
      }

      return next;
    });

    setQuoteResult(null);
    setQuoteError("");
    setShowApplication(false);
  };

  const setOwnerValue = (name: keyof OwnerForm, value: string) => {
    setOwner((prev) => ({ ...prev, [name]: value }));
    setOwnerErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const setDogValue = (
    index: number,
    name: keyof DogForm,
    value: string
  ) => {
    setDogs((prev) =>
      prev.map((dog, dogIndex) => {
        if (dogIndex !== index) return dog;

        const next = { ...dog, [name]: value };

        if (name === "sex") {
          if (value === "reu") {
            next.femaleStatus = "";
            next.heatHistoryStatus = "";
            next.lastHeatDate = "";
            next.nextHeatMonth = "";
          } else if (value === "teef") {
            next.castrationStatus = "";
          }
        }

        if (name === "femaleStatus" && value === "gesteriliseerd") {
          next.heatHistoryStatus = "";
          next.lastHeatDate = "";
          next.nextHeatMonth = "";
        }

        if (name === "heatHistoryStatus" && value === "nog_niet") {
          next.lastHeatDate = "";
        }

        return next;
      })
    );

    setDogErrors((prev) =>
      prev.map((entry, dogIndex) =>
        dogIndex === index
          ? { ...entry, [name]: undefined }
          : entry
      )
    );
  };

  async function calculateQuote() {
    setQuoteError("");
    setQuoteResult(null);
    setShowApplication(false);

    if (!supabase) {
      setQuoteError("De prijsberekening is tijdelijk niet beschikbaar.");
      return;
    }

    if (!quoteForm.serviceType) {
      setQuoteError("Kies eerst het soort opvang.");
      return;
    }

    if (!quoteForm.startDate) {
      setQuoteError("Kies eerst de gewenste opvangdatum.");
      return;
    }

    if (
      quoteForm.serviceType === "logeren" &&
      (!quoteForm.endDate || quoteForm.endDate <= quoteForm.startDate)
    ) {
      setQuoteError("Kies een geldige vertrekdatum na de aankomstdatum.");
      return;
    }

    setQuoteBusy(true);

    const { data, error: rpcError } = await supabase.rpc(
      "public_intake_price_preview_detailed_v1",
      {
        p_service_type: quoteForm.serviceType,
        p_start_date: quoteForm.startDate,
        p_end_date: quoteIsSingleDay
          ? quoteForm.startDate
          : quoteForm.endDate,
        p_dog_count: Number(quoteForm.dogCount),
        p_arrival_time:
          quoteForm.serviceType === "logeren"
            ? quoteForm.arrivalTime
            : null,
        p_departure_time:
          quoteForm.serviceType === "logeren"
            ? quoteForm.departureTime
            : null,
      }
    );

    setQuoteBusy(false);

    if (rpcError) {
      setQuoteError(rpcError.message);
      return;
    }

    setQuoteResult(data as QuoteResult);
  }

  function continueWithApplication() {
    if (!quoteResult) return;

    const count = quoteResult.dog_count;

    setDogs((prev) =>
      Array.from({ length: count }, (_, index) =>
        prev[index] ? prev[index] : emptyDog()
      )
    );
    setDogErrors(Array.from({ length: count }, () => ({})));
    setShowApplication(true);

    window.setTimeout(() => {
      document
        .getElementById("opvangaanvraag-gegevens")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  const validateApplication = () => {
    const nextOwnerErrors: Partial<
      Record<keyof OwnerForm, string>
    > = {};

    if (owner.firstName.trim().length < 2)
      nextOwnerErrors.firstName = "Vul je voornaam in.";
    if (owner.lastName.trim().length < 2)
      nextOwnerErrors.lastName = "Vul je achternaam in.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email))
      nextOwnerErrors.email = "Vul een geldig e-mailadres in.";
    if (owner.phone.replace(/\D/g, "").length < 8)
      nextOwnerErrors.phone = "Vul een geldig telefoonnummer in.";

    const nextDogErrors: DogError[] = dogs.map((dog) => {
      const entry: DogError = {};
      const isMix = dog.breed.toLowerCase().includes("kruising");
      const isMale = dog.sex === "reu";
      const isFemale = dog.sex === "teef";
      const isIntactFemale =
        isFemale && dog.femaleStatus === "niet_gesteriliseerd";

      if (dog.name.trim().length < 2)
        entry.name = "Vul de naam van de hond in.";
      if (dog.breed.trim().length < 2)
        entry.breed = "Kies of vul het ras in.";
      if (isMix && dog.mixDetails.trim().length < 2)
        entry.mixDetails = "Vul in welke rassen bekend zijn.";
      if (!dog.sex) entry.sex = "Kies reu of teef.";
      if (isMale && !dog.castrationStatus)
        entry.castrationStatus = "Kies de castratiestatus.";
      if (isFemale && !dog.femaleStatus)
        entry.femaleStatus = "Kies of de teef gesteriliseerd is.";
      if (isIntactFemale && !dog.heatHistoryStatus)
        entry.heatHistoryStatus =
          "Geef aan of de teef al loops is geweest.";
      if (
        isIntactFemale &&
        dog.heatHistoryStatus === "wel_geweest" &&
        !dog.lastHeatDate
      ) {
        entry.lastHeatDate =
          "Vul de datum van de laatste loopsheid in.";
      }

      return entry;
    });

    setOwnerErrors(nextOwnerErrors);
    setDogErrors(nextDogErrors);

    return (
      Object.keys(nextOwnerErrors).length === 0 &&
      nextDogErrors.every((entry) => Object.keys(entry).length === 0)
    );
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitted(false);

    if (!quoteResult) {
      setError("Bereken eerst de prijs en beschikbaarheid.");
      return;
    }

    if (!validateApplication()) return;

    if (anyExcluded) {
      setError(friendlyBreedMessage);
      return;
    }

    if (!supabase) {
      setError("De aanvraagfunctie is tijdelijk niet beschikbaar.");
      return;
    }

    const dogsPayload = dogs.map((dog) => {
      const isFemale = dog.sex === "teef";
      const isIntactFemale =
        isFemale && dog.femaleStatus === "niet_gesteriliseerd";

      return {
        name: dog.name.trim(),
        breed: dog.breed.trim(),
        mix_details: dog.mixDetails.trim() || null,
        sex: dog.sex,
        birth_date: dog.birthDate || null,
        castration_status:
          dog.sex === "reu" ? dog.castrationStatus : null,
        female_spayed:
          isFemale ? dog.femaleStatus === "gesteriliseerd" : null,
        never_in_heat:
          isIntactFemale &&
          dog.heatHistoryStatus === "nog_niet",
        last_heat_date:
          isIntactFemale &&
          dog.heatHistoryStatus === "wel_geweest"
            ? dog.lastHeatDate
            : null,
        next_heat_month:
          isIntactFemale && dog.nextHeatMonth
            ? `${dog.nextHeatMonth}-01`
            : null,
      };
    });

    const quoteContext = [
      `Prijsberekening website: ${money(quoteResult.total_amount)}.`,
      `Voorlopige beschikbaarheid: ${quoteResult.availability_message}`,
      quoteResult.service_type === "logeren"
        ? `Gewenste tijden: brengen ${
            quoteForm.arrivalTime === "14:00"
              ? "na 14:00"
              : quoteForm.arrivalTime
          } uur, ophalen ${
            quoteForm.departureTime === "10:00"
              ? "vóór 10:00"
              : quoteForm.departureTime
          } uur.`
        : null,
      `Aantal honden: ${quoteResult.dog_count}.`,
    ]
      .filter(Boolean)
      .join("\n");

    const combinedMessage = [quoteContext, owner.message.trim()]
      .filter(Boolean)
      .join("\n\n");

    setBusy(true);

    const { data, error: rpcError } = await supabase.rpc(
      "submit_intake_request_v3",
      {
        p_first_name: owner.firstName.trim(),
        p_last_name: owner.lastName.trim(),
        p_email: owner.email.trim().toLowerCase(),
        p_phone: owner.phone.trim(),
        p_dogs: dogsPayload,
        p_service_type: quoteResult.service_type,
        p_start_date: quoteResult.start_date,
        p_end_date: quoteResult.end_date,
        p_message: combinedMessage || null,
      }
    );

    if (rpcError) {
      setBusy(false);
      setError(rpcError.message);
      return;
    }

    if (data?.allowed === false) {
      setBusy(false);
      setError(data?.message || friendlyBreedMessage);
      return;
    }

    const dogSummary = dogsPayload
      .map((dog, index) => {
        const breed = dog.mix_details
          ? `${dog.breed} – ${dog.mix_details}`
          : dog.breed;
        return `Hond ${index + 1}: ${dog.name} (${breed}, ${dog.sex})`;
      })
      .join("\n");

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID!,
        {
          to_email: "pippaya.nl@gmail.com",
          to_name: "Pippaya",
          from_name: `${owner.firstName} ${owner.lastName}`,
          from_email: owner.email,
          from_phone: owner.phone,
          pet_name: dogsPayload.map((dog) => dog.name).join(", "),
          dog_breed: dogsPayload
            .map((dog) =>
              dog.mix_details
                ? `${dog.breed} – ${dog.mix_details}`
                : dog.breed
            )
            .join(", "),
          dog_sex: dogsPayload.map((dog) => dog.sex).join(", "),
          service_type: quoteResult.service_type,
          start_date: quoteResult.start_date,
          end_date: quoteResult.end_date,
          message: `${dogSummary}\n\n${combinedMessage}`,
        }
      )
      .catch(() => undefined);

    setSubmitted(true);
    setBusy(false);
    setOwner(emptyOwner);
    setDogs([emptyDog()]);
    setDogErrors([{}]);
    setQuoteForm(emptyQuote);
    setQuoteResult(null);
    setShowApplication(false);
  }

  const availabilityTitle = quoteResult
    ? quoteResult.availability_status === "available"
      ? "Deze periode lijkt beschikbaar"
      : quoteResult.availability_status === "manual_review"
        ? "Deze combinatie beoordelen we graag persoonlijk"
        : "Deze periode is momenteel niet beschikbaar"
    : "";

  const availabilityText = quoteResult
    ? quoteResult.availability_status === "available"
      ? "Op basis van de huidige bezetting is er op dit moment ruimte. Dit is nog geen reservering; na je aanvraag beoordelen we of Pippaya bij jouw hond past en plannen we zo nodig eerst een kennismaking."
      : quoteResult.availability_status === "manual_review"
        ? quoteResult.availability_message
        : quoteResult.availability_message ||
          "Op basis van de huidige planning is er voor deze datum of periode onvoldoende ruimte."
    : "";

  return (
    <FormPaper component="form" onSubmit={submit}>
      <ExistingCustomerBox>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">Ben je al klant bij Pippaya?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Komt je hond al bij Pippaya? Log dan in bij Mijn Pippaya. Heb je je
              account nog niet geactiveerd? Dan kun je dat hier doen.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Button
              type="button"
              variant="outlined"
              startIcon={<LoginRoundedIcon />}
              onClick={() => navigate("/inloggen")}
            >
              Inloggen
            </Button>

            <Button
              type="button"
              variant="contained"
              onClick={() => navigate("/mijn-pippaya/activeren")}
            >
              Account activeren
            </Button>
          </Stack>
        </Stack>
      </ExistingCustomerBox>

      <Typography variant="h5" sx={{ mb: 1.5 }}>
        Waarmee kunnen we je helpen?
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        sx={{ mb: 3 }}
      >
        <Button
          type="button"
          variant={contactMode === "booking" ? "contained" : "outlined"}
          onClick={() => {
            setContactMode("booking");
            setInformationError("");
            setInformationSubmitted(false);
          }}
        >
          Prijs & beschikbaarheid
        </Button>

        <Button
          type="button"
          variant={contactMode === "information" ? "contained" : "outlined"}
          onClick={() => {
            setContactMode("information");
            setError("");
            setSubmitted(false);
          }}
        >
          Alleen informatie / een vraag
        </Button>
      </Stack>

      {contactMode === "information" && (
        <QuoteBox>
          <Typography variant="h4">Een vraag stellen</Typography>

          <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
            Wil je alleen iets weten en nog geen prijs berekenen of opvang
            aanvragen? Stuur hier je vraag. Er wordt geen plek gereserveerd en
            er wordt geen offerte of factuur aangemaakt.
          </Typography>

          {informationSubmitted && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Bedankt. Je vraag is ontvangen. Ik neem zo snel mogelijk contact
              met je op.
            </Alert>
          )}

          {informationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {informationError}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Voornaam"
                value={information.firstName}
                onChange={(e) =>
                  setInformationValue("firstName", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Achternaam"
                value={information.lastName}
                onChange={(e) =>
                  setInformationValue("lastName", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="E-mailadres"
                value={information.email}
                onChange={(e) =>
                  setInformationValue("email", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefoonnummer (optioneel)"
                value={information.phone}
                onChange={(e) =>
                  setInformationValue("phone", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Naam hond (optioneel)"
                value={information.dogName}
                onChange={(e) =>
                  setInformationValue("dogName", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ras / type hond (optioneel)"
                value={information.dogBreed}
                onChange={(e) =>
                  setInformationValue("dogBreed", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Onderwerp (optioneel)</InputLabel>
                <Select
                  label="Onderwerp (optioneel)"
                  value={information.topic}
                  onChange={(e) =>
                    setInformationValue("topic", String(e.target.value))
                  }
                >
                  <MenuItem value="">Geen keuze</MenuItem>
                  <MenuItem value="werkwijze">Werkwijze Pippaya</MenuItem>
                  <MenuItem value="kennismaking">Kennismaking</MenuItem>
                  <MenuItem value="voorwaarden">Voorwaarden / beleid</MenuItem>
                  <MenuItem value="beschikbaarheid">Beschikbaarheid</MenuItem>
                  <MenuItem value="anders">Anders</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Soort opvang (optioneel)</InputLabel>
                <Select
                  label="Soort opvang (optioneel)"
                  value={information.serviceType}
                  onChange={(e) =>
                    setInformationValue(
                      "serviceType",
                      String(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">Nog niet bekend</MenuItem>
                  <MenuItem value="dagopvang">Dagopvang</MenuItem>
                  <MenuItem value="logeren">Logeren</MenuItem>
                  <MenuItem value="feestdagopvang">Feestdagopvang</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Vanaf datum (optioneel)"
                InputLabelProps={{ shrink: true }}
                value={information.startDate}
                onChange={(e) =>
                  setInformationValue("startDate", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Tot en met (optioneel)"
                InputLabelProps={{ shrink: true }}
                value={information.endDate}
                onChange={(e) =>
                  setInformationValue("endDate", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Waar wil je informatie over?"
                value={information.question}
                onChange={(e) =>
                  setInformationValue("question", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <Button
            type="button"
            variant="contained"
            size="large"
            startIcon={<SendRoundedIcon />}
            sx={{ mt: 2.5 }}
            disabled={informationBusy}
            onClick={() => void submitInformationRequest()}
          >
            {informationBusy ? "Versturen..." : "Vraag versturen"}
          </Button>
        </QuoteBox>
      )}

      {contactMode === "booking" && (
        <>
      <Stack direction="row" spacing={1} alignItems="center">
        <CalculateRoundedIcon color="primary" />
        <Typography variant="h4">Eerst weten wat het kost?</Typography>
      </Stack>

      <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
        Kies hieronder de gewenste opvang. Je ziet direct de prijs op basis van
        de huidige tarieven én een voorlopige indicatie van de beschikbaarheid.
        Er wordt hiermee nog geen opvang aangevraagd en er wordt geen plek
        gereserveerd.
      </Typography>

      <QuoteBox sx={{ mt: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Soort opvang</InputLabel>
              <Select
                label="Soort opvang"
                value={quoteForm.serviceType}
                onChange={(e) =>
                  setQuoteValue("serviceType", String(e.target.value))
                }
              >
                <MenuItem value="dagopvang">Dagopvang</MenuItem>
                <MenuItem value="logeren">Logeren</MenuItem>
                <MenuItem value="feestdagopvang">Feestdagopvang</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Aantal honden</InputLabel>
              <Select
                label="Aantal honden"
                value={quoteForm.dogCount}
                onChange={(e) =>
                  setQuoteValue("dogCount", String(e.target.value))
                }
              >
                <MenuItem value="1">1 hond</MenuItem>
                <MenuItem value="2">2 honden uit hetzelfde gezin</MenuItem>
                <MenuItem value="3">3 honden uit hetzelfde gezin</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            sm={quoteForm.serviceType === "logeren" ? 6 : 12}
          >
            <TextField
              fullWidth
              type="date"
              label={
                quoteForm.serviceType === "logeren"
                  ? "Aankomstdatum"
                  : "Opvangdatum"
              }
              InputLabelProps={{ shrink: true }}
              value={quoteForm.startDate}
              onChange={(e) => setQuoteValue("startDate", e.target.value)}
            />
          </Grid>

          {quoteForm.serviceType === "logeren" && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Vertrekdatum"
                InputLabelProps={{ shrink: true }}
                value={quoteForm.endDate}
                onChange={(e) => setQuoteValue("endDate", e.target.value)}
              />
            </Grid>
          )}

          {quoteForm.serviceType === "logeren" && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Brengtijd</InputLabel>
                  <Select
                    label="Brengtijd"
                    value={quoteForm.arrivalTime}
                    onChange={(e) =>
                      setQuoteValue("arrivalTime", String(e.target.value))
                    }
                  >
                    {arrivalOptions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Brengen kan vanaf 08:00 uur. Vanaf 14:00 uur is inbegrepen.
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Ophaaltijd</InputLabel>
                  <Select
                    label="Ophaaltijd"
                    value={quoteForm.departureTime}
                    onChange={(e) =>
                      setQuoteValue("departureTime", String(e.target.value))
                    }
                  >
                    {departureOptions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Tot 10:00 uur inbegrepen. Na 12:00 uur wordt dagopvang op
                    de vertrekdag berekend.
                  </FormHelperText>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>

        <Button
          type="button"
          fullWidth
          variant="contained"
          size="large"
          startIcon={<CalculateRoundedIcon />}
          onClick={calculateQuote}
          disabled={quoteBusy}
          sx={{ mt: 2.5 }}
        >
          {quoteBusy ? "Prijs berekenen..." : "Bekijk prijs & beschikbaarheid"}
        </Button>

        {quoteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {quoteError}
          </Alert>
        )}

        {quoteResult && (
          <Stack spacing={1.5} sx={{ mt: 2.5 }}>
            <Alert
              severity={
                quoteResult.availability_status === "available"
                  ? "success"
                  : quoteResult.availability_status === "manual_review"
                    ? "info"
                    : "warning"
              }
            >
              <Typography fontWeight={800}>{availabilityTitle}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {availabilityText}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, boxShadow: "none" }}>
              <Typography variant="h6">Prijsopbouw</Typography>

              <Stack spacing={1} sx={{ mt: 1.25 }}>
                {quoteResult.price_lines.map((line, index) => (
                  <Stack
                    key={`${line.description}-${index}`}
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Box>
                      <Typography fontWeight={700}>
                        {line.description}
                      </Typography>
                      {line.discount_percent > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {line.discount_percent}% gezinskorting verwerkt
                        </Typography>
                      )}
                    </Box>
                    <Typography fontWeight={800}>
                      {money(line.total)}
                    </Typography>
                  </Stack>
                ))}

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="h6">Totaal</Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {money(quoteResult.total_amount)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Typography variant="body2" color="text.secondary">
              {quoteResult.note}
            </Typography>

            {quoteResult.availability_status !== "unavailable" && (
              <Button
                type="button"
                variant="outlined"
                size="large"
                endIcon={<ArrowDownwardRoundedIcon />}
                onClick={continueWithApplication}
              >
                Ik wil hiervoor opvang aanvragen
              </Button>
            )}
          </Stack>
        )}
      </QuoteBox>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {submitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Dank je wel. Je opvangaanvraag is ontvangen. Ik bekijk of de hond
          {dogs.length > 1 ? "en" : ""} en de gewenste periode binnen Pippaya
          passen en neem contact met je op.
        </Alert>
      )}

      {showApplication && quoteResult && (
        <Box id="opvangaanvraag-gegevens">
          <Stack direction="row" spacing={1} alignItems="center">
            <PetsRoundedIcon color="primary" />
            <Typography variant="h4">Opvang aanvragen</Typography>
          </Stack>

          <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
            Vul hieronder je contactgegevens en de gegevens van
            {quoteResult.dog_count === 1
              ? " je hond"
              : ` je ${quoteResult.dog_count} honden`} in. De gekozen
            opvangperiode uit de prijsberekening wordt automatisch meegenomen.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            Een aanvraag is nog geen definitieve reservering. Ik kijk na de
            aanvraag ook naar de groepssamenstelling en voor een eerste verblijf
            maken we in principe eerst kennis.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mt: 2, boxShadow: "none" }}>
            <Typography fontWeight={800}>
              {quoteResult.service_type === "dagopvang"
                ? `Dagopvang op ${quoteResult.start_date}`
                : quoteResult.service_type === "feestdagopvang"
                  ? `Feestdagopvang op ${quoteResult.start_date}`
                  : `Logeren van ${quoteResult.start_date} t/m ${quoteResult.end_date}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {quoteResult.dog_count} hond
              {quoteResult.dog_count === 1 ? "" : "en"} · berekende prijs{" "}
              {money(quoteResult.total_amount)}
            </Typography>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Voornaam"
                value={owner.firstName}
                onChange={(e) => setOwnerValue("firstName", e.target.value)}
                error={!!ownerErrors.firstName}
                helperText={ownerErrors.firstName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Achternaam"
                value={owner.lastName}
                onChange={(e) => setOwnerValue("lastName", e.target.value)}
                error={!!ownerErrors.lastName}
                helperText={ownerErrors.lastName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="E-mailadres"
                value={owner.email}
                onChange={(e) => setOwnerValue("email", e.target.value)}
                error={!!ownerErrors.email}
                helperText={ownerErrors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Telefoonnummer"
                value={owner.phone}
                onChange={(e) => setOwnerValue("phone", e.target.value)}
                error={!!ownerErrors.phone}
                helperText={ownerErrors.phone}
              />
            </Grid>
          </Grid>

          {dogs.map((dog, index) => {
            const errors = dogErrors[index] ?? {};
            const isMix = dog.breed.toLowerCase().includes("kruising");
            const isMale = dog.sex === "reu";
            const isFemale = dog.sex === "teef";
            const isIntactFemale =
              isFemale && dog.femaleStatus === "niet_gesteriliseerd";

            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 2.5, mt: 3, boxShadow: "none" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PetsRoundedIcon color="primary" />
                  <Typography variant="h5">
                    Hond {index + 1}
                  </Typography>
                </Stack>

                <Grid container spacing={2.5} sx={{ mt: 0.25 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Naam hond"
                      value={dog.name}
                      onChange={(e) =>
                        setDogValue(index, "name", e.target.value)
                      }
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={breedSuggestions}
                      value={dog.breed}
                      onChange={(_, value) =>
                        setDogValue(index, "breed", value || "")
                      }
                      onInputChange={(_, value) =>
                        setDogValue(index, "breed", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Ras of type"
                          error={!!errors.breed}
                          helperText={
                            errors.breed ||
                            "Je kunt ook zelf een ras typen."
                          }
                        />
                      )}
                    />
                  </Grid>

                  {isMix && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Welke rassen zitten voor zover bekend in de kruising?"
                        value={dog.mixDetails}
                        onChange={(e) =>
                          setDogValue(index, "mixDetails", e.target.value)
                        }
                        error={!!errors.mixDetails}
                        helperText={errors.mixDetails}
                      />
                    </Grid>
                  )}

                  {dog.breed &&
                    dogOutcomes[index] === "excluded" && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          {friendlyBreedMessage}
                        </Alert>
                      </Grid>
                    )}

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required error={!!errors.sex}>
                      <InputLabel>Geslacht</InputLabel>
                      <Select
                        label="Geslacht"
                        value={dog.sex}
                        onChange={(e) =>
                          setDogValue(index, "sex", String(e.target.value))
                        }
                      >
                        <MenuItem value="reu">Reu</MenuItem>
                        <MenuItem value="teef">Teef</MenuItem>
                      </Select>
                      <FormHelperText>{errors.sex}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Geboortedatum"
                      InputLabelProps={{ shrink: true }}
                      value={dog.birthDate}
                      onChange={(e) =>
                        setDogValue(index, "birthDate", e.target.value)
                      }
                    />
                  </Grid>

                  {isMale && (
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.castrationStatus}
                      >
                        <InputLabel>Castratiestatus</InputLabel>
                        <Select
                          label="Castratiestatus"
                          value={dog.castrationStatus}
                          onChange={(e) =>
                            setDogValue(
                              index,
                              "castrationStatus",
                              String(e.target.value)
                            )
                          }
                        >
                          <MenuItem value="gecastreerd">
                            Gecastreerd
                          </MenuItem>
                          <MenuItem value="chemisch">
                            Chemisch gecastreerd
                          </MenuItem>
                          <MenuItem value="intact">
                            Niet gecastreerd
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          {errors.castrationStatus}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  {isFemale && (
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.femaleStatus}
                      >
                        <InputLabel>Status teef</InputLabel>
                        <Select
                          label="Status teef"
                          value={dog.femaleStatus}
                          onChange={(e) =>
                            setDogValue(
                              index,
                              "femaleStatus",
                              String(e.target.value)
                            )
                          }
                        >
                          <MenuItem value="gesteriliseerd">
                            Gesteriliseerd
                          </MenuItem>
                          <MenuItem value="niet_gesteriliseerd">
                            Niet gesteriliseerd
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          {errors.femaleStatus}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  {isIntactFemale && (
                    <>
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Loopse teven kunnen niet bij Pippaya worden
                          opgevangen. Verandert de verwachte loopsheid na de
                          aanvraag of reservering, geef dit dan zo snel
                          mogelijk door.
                        </Alert>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <FormControl
                          fullWidth
                          required
                          error={!!errors.heatHistoryStatus}
                        >
                          <InputLabel>Loopsheid tot nu toe</InputLabel>
                          <Select
                            label="Loopsheid tot nu toe"
                            value={dog.heatHistoryStatus}
                            onChange={(e) =>
                              setDogValue(
                                index,
                                "heatHistoryStatus",
                                String(e.target.value)
                              )
                            }
                          >
                            <MenuItem value="nog_niet">
                              Nog niet loops geweest
                            </MenuItem>
                            <MenuItem value="wel_geweest">
                              Is al loops geweest
                            </MenuItem>
                          </Select>
                          <FormHelperText>
                            {errors.heatHistoryStatus}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {dog.heatHistoryStatus === "wel_geweest" && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            required
                            type="date"
                            label="Laatste loopsheid"
                            InputLabelProps={{ shrink: true }}
                            value={dog.lastHeatDate}
                            onChange={(e) =>
                              setDogValue(
                                index,
                                "lastHeatDate",
                                e.target.value
                              )
                            }
                            error={!!errors.lastHeatDate}
                            helperText={errors.lastHeatDate}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="month"
                          label="Verwachte volgende loopsheid"
                          InputLabelProps={{ shrink: true }}
                          value={dog.nextHeatMonth}
                          onChange={(e) =>
                            setDogValue(
                              index,
                              "nextHeatMonth",
                              e.target.value
                            )
                          }
                          helperText="Maand is voldoende. Laat leeg als je het niet weet."
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            );
          })}

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Vertel iets over je hond(en)"
            placeholder="Bijvoorbeeld omgang met andere honden, gedrag, bijzonderheden of eerdere opvangervaring."
            value={owner.message}
            onChange={(e) => setOwnerValue("message", e.target.value)}
            sx={{ mt: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            startIcon={<SendRoundedIcon />}
            disabled={busy || anyExcluded}
            sx={{ mt: 3 }}
          >
            {busy ? "Aanvraag versturen..." : "Verstuur opvangaanvraag"}
          </Button>
        </Box>
      )}
    </FormPaper>
  );
};
